/**
 * Resolves a CMS-supplied URL (absolute, root-relative, or empty) to an
 * absolute URL on the given site origin. The host is always forced to `origin`
 * so a CMS value pointing at a staging or preview host can never leak into a
 * canonical link or og:url tag. Used for canonical links, og:url and
 * structured-data URLs.
 */
export function toAbsoluteUrl(value: string, origin: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return origin;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      return `${origin}${new URL(trimmed).pathname}`;
    } catch {
      return origin;
    }
  }
  return `${origin}${trimmed.startsWith("/") ? trimmed : `/${trimmed}`}`;
}

/**
 * Resolves a CMS-supplied media URL to an absolute URL. Unlike
 * `toAbsoluteUrl`, an already-absolute http(s) URL keeps its own host —
 * Umbraco media may live on another domain — so only root-relative paths are
 * prefixed with the site origin. Empty input resolves to `undefined` so
 * callers can simply omit the tag. Mirrors the private `absoluteMediaUrl`
 * helper in seo-schema.ts.
 */
export function toAbsoluteMediaUrl(
  url: string | null | undefined,
  origin: string,
): string | undefined {
  const trimmed = url?.trim();
  if (!trimmed) {
    return undefined;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `${origin}${trimmed.startsWith("/") ? trimmed : `/${trimmed}`}`;
}
